import {KeyboardListener} from "../../../../utils/interaction/key/KeyboardListener";
import {ObjectUtils} from "../../../../utils/data/ObjectUtils";
import {HTMLUtils} from "../../../../utils/HTML/HTMLUtils";

/**
 * You should use one of the descendants of this class: WarningWindow, ConfirmWindow, SpaceExportWindow, or PromptWindow
 * Example: const confirmed = await ConfirmWindow.open("Are you sure you want to delete this item?");
 */

const PopupWindowClassName = "PopupWindow";

export interface IPopupWindowConfig {
	className?: string;
	backdrop?: boolean;
	ok?: string; // The label of the OK button
	cancel?: string; // The label of the Cancel button
	parentElement?: HTMLElement;
	additionalElements?: HTMLElement;
	disableButtonOnEmptyInput?: boolean;
}

interface IPopupWindowProps {
	title: string;
	message: string;
	config?: IPopupWindowConfig;
}

export abstract class PopupWindow<T> {
	private _isOkButtonEnabled: boolean = true;
	private _container: HTMLDivElement = document.createElement("div");
	protected _okButton: HTMLDivElement = document.createElement("div");
	private readonly _props: IPopupWindowProps;
	protected readonly _config: IPopupWindowConfig;

	private static _numberOfOpenPopups: number = 0;
	protected static readonly _defaultConfig: IPopupWindowConfig = {
		className: "",
		backdrop: true,
		ok: "Yes",
		cancel: "No",
		parentElement: document.body,
	};
	protected abstract _okValue: T | T[]; // the return value when the user clicks "ok"
	protected abstract _cancelValue: T; // the return value when the user clicks "cancel"
	protected _exportInputs: HTMLElement[] = [];
	protected resolve: (isOk?: T | T[]) => void;

	constructor(props: IPopupWindowProps) {
		this._props = props;
		this._config = ObjectUtils.mergeConfig(PopupWindow._defaultConfig, props.config || {});
	}

	private onKeyDown = (event: KeyboardEvent) => {
		switch (event.key) {
			case KeyboardListener.KEY_ENTER:
				if (this._isOkButtonEnabled) {
					this.onOkClick(event);
				}
				break;
			case KeyboardListener.KEY_ESCAPE:
				this.onCloseClick(event);
				break;
		}
	};

	protected close() {
		KeyboardListener.getInstance().signals.down.remove(this.onKeyDown);
		HTMLUtils.detach(this._container);

		PopupWindow._numberOfOpenPopups = Math.max(0, PopupWindow._numberOfOpenPopups - 1);
	}

	private onCloseClick = (event: MouseEvent | KeyboardEvent) => {
		event.stopImmediatePropagation();
		this.close();
		this.resolve(null);
	};

	private onCancelClick = (event: MouseEvent | KeyboardEvent) => {
		event.stopImmediatePropagation();
		this.close();
		this.resolve(this._cancelValue);
	};

	private onOkClick = (event: MouseEvent | KeyboardEvent) => {
		event.stopImmediatePropagation();
		if (this._isOkButtonEnabled) {
			this.close();
			this.resolve(this._okValue);
		}
	};

	protected enableOkButton() {
		this._isOkButtonEnabled = true;
		this._okButton.classList.remove("disabled");
	}

	protected disableOkButton() {
		this._isOkButtonEnabled = false;
		this._okButton.classList.add("disabled");
	}

	protected open() {
		KeyboardListener.getInstance().signals.down.add(this.onKeyDown);

		this.draw();

		return new Promise<T | T[]>((resolve, reject) => {
			this.resolve = resolve;
		});
	}

	private draw() {
		this._container.className = "popupBackdrop flexCenter";
		if (this._config.backdrop) {
			this._container.onclick = this.onCloseClick;
		}

		const popupWindow = document.createElement("div");

		popupWindow.className = `${PopupWindowClassName} ${this._config.className ?? ""}`;

		const title = document.createElement("div");

		title.className = "title hbox alignCenter";
		title.textContent = this._props.title;
		popupWindow.appendChild(title);

		const message = document.createElement("div");

		message.className = "message";
		message.innerHTML = this._props.message;
		popupWindow.appendChild(message);

		if (this._config.additionalElements) {
			popupWindow.appendChild(this._config.additionalElements);
		}

		if (this._exportInputs.length > 0) {
			const inputContainer = document.createElement("div");

			inputContainer.className = "InputContainer";
			popupWindow.appendChild(inputContainer);
			this._exportInputs.forEach((el) => inputContainer.appendChild(el));
		}

		const buttonContainer = document.createElement("div");

		buttonContainer.className = "bottom hbox flexCenter justifyEnd";

		if (this._config.cancel) {
			const closeBtn = document.createElement("div");

			closeBtn.className = "closeBtn";
			closeBtn.innerHTML = "<svg class='icon'><use xlink:href='#icon-close'></use></svg>";
			closeBtn.onclick = this.onCloseClick;
			title.appendChild(closeBtn);

			const cancelButton = document.createElement("div");

			cancelButton.className = "Button";
			this._okButton.setAttribute("data-cy", "CancelButton");
			cancelButton.textContent = this._config.cancel;
			cancelButton.onclick = this.onCancelClick;
			buttonContainer.appendChild(cancelButton);
		}

		this._okButton.className = "primary Button";
		this._okButton.textContent = this._config.ok;
		this._okButton.setAttribute("data-cy", "OkButton");
		this._okButton.onclick = this.onOkClick;

		if (!this._isOkButtonEnabled) {
			this._okButton.classList.add("disabled");
		}

		buttonContainer.appendChild(this._okButton);

		popupWindow.appendChild(buttonContainer);

		this._container.appendChild(popupWindow);

		this._config.parentElement.appendChild(this._container);

		PopupWindow._numberOfOpenPopups++;
	}

	public static get numberOfOpenPopups() {
		return PopupWindow._numberOfOpenPopups;
	}
}
